import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import QuotationTable from "./QuotationTable";
import Icons from "../../../../Assets/Icons";
import ToastPopup from "../../../../widgets/Toast/toastPopup.js";
import { uploadMedia } from "../../../../modules/patientProfile/patientProfileThunk";
import { extractUploadedFileUrl } from "../../../../utils/fileHelpers";
import { getLocalizedTitle } from "../../../../utils/localizedText";
import { openSecureFileUrl } from "../../../../utils/secureFilePreview";
import "./QuotationDetails.css";

const buildItemsFromVisits = (visits, distanceInKm) => {
  return (visits || []).map((v) => {
    const price = v.serviceItemData?.price;
    const unitFee = parseFloat(price?.wh?.opdInternationalPrice || price?.wh?.opdLocalPrice || 0);
    const dateRange = v.isMultipleDay ? `${v.startDate} - ${v.endDate}` : v.singleDate || "";
    const serviceItemTitle = v.serviceItemData?.title || v.serviceItemTitle || v.serviceItemName;

    return {
      serviceName: getLocalizedTitle(serviceItemTitle) || "-",
      serviceItemTitle,
      serviceItemData: v.serviceItemData || null,
      dateRange,
      startTime: v.startTime || "",
      endTime: v.endTime || "",
      distanceInKm,
      unitFee,
      transportationFee: "",
      adjustment: "",
    };
  });
};

const QuotationDetails = ({ setPage, visitsData, distanceInKm, onFinalSubmit }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [quotationItems, setQuotationItems] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [quotationURL, setQuotationURL] = useState("");
  const [isUploadingQuotation, setIsUploadingQuotation] = useState(false);

  useEffect(() => {
    if (visitsData?.length) {
      setQuotationItems(buildItemsFromVisits(visitsData, distanceInKm));
    }
  }, [visitsData, distanceInKm]);

  const handleUnitFeeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, unitFee: value } : item)));
  };

  const handleTransportationFeeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, transportationFee: value } : item)));
  };

  const handleAdjustmentChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, adjustment: value } : item)));
  };

  useEffect(() => {
    return () => {
      if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    };
  }, [pdfPreview]);

  const setSelectedQuotationFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      ToastPopup.error(t("pleaseUploadPdfOnly"));
      return;
    }

    if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    const localPreviewUrl = URL.createObjectURL(file);
    setPdfFile(file);
    setPdfPreview(localPreviewUrl);
    setQuotationURL("");
    setIsUploadingQuotation(true);

    try {
      const result = await dispatch(uploadMedia({ files: [file] }));
      if (result.meta.requestStatus !== "fulfilled") {
        throw new Error(result.error?.message || "Upload failed");
      }

      const uploadedUrl = extractUploadedFileUrl(result.payload);
      if (!uploadedUrl) {
        throw new Error("Upload completed without a file URL");
      }

      setQuotationURL(uploadedUrl);
    } catch (error) {
      URL.revokeObjectURL(localPreviewUrl);
      setPdfFile(null);
      setPdfPreview(null);
      setQuotationURL("");
      ToastPopup.error(error.message || t("bulkUploadFailedGeneric") || "Upload failed");
    } finally {
      setIsUploadingQuotation(false);
    }
  };

  const handleFileUpload = (e) => {
    setSelectedQuotationFile(e.target.files[0]);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setSelectedQuotationFile(e.dataTransfer.files[0]);
  };

  const handleRemovePdf = () => {
    if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    setPdfFile(null);
    setPdfPreview(null);
    setQuotationURL("");
  };

  const handleSendQuote = () => {
    const uploadedQuotationURL = quotationURL.trim();
    if (isUploadingQuotation) {
      ToastPopup.error(t("loading") || "Please wait for upload to finish");
      return;
    }
    if (!uploadedQuotationURL) {
      ToastPopup.error(t("pleaseUploadQuotationPdf") || "Please upload quotation first");
      return;
    }
    // Price = total from the quotation table
    const total = quotationItems.reduce((sum, item) => sum + (parseFloat(item.unitFee) || 0) + (parseFloat(item.transportationFee) || 0) + (parseFloat(item.adjustment) || 0), 0);
    const localizedQuotationItems = quotationItems.map((item) => ({
      ...item,
      serviceName: getLocalizedTitle(item.serviceItemData || item.serviceItemTitle, i18n.language) || item.serviceName,
    }));
    onFinalSubmit?.({ quotationURL: uploadedQuotationURL, price: String(total), quotationItems: localizedQuotationItems });
    ToastPopup.success(t("quoteSent"));
  };

  return (
    <div className="quotation-details-container">
      {/* Quotation Table */}
      <div className="whp-container quotation-table-card">
        <div className="title-hr2">
          <p>{t("quotationDetails")}</p>
          <hr />
        </div>
        <QuotationTable items={quotationItems} onUnitFeeChange={handleUnitFeeChange} onTransportationFeeChange={handleTransportationFeeChange} onAdjustmentChange={handleAdjustmentChange} />
      </div>

      {/* Upload Quotation */}
      <div className="whp-container pdf-upload-card">
        <div className="title-hr2">
          <p>
            {t("uploadQuotationDocument")} <label>*</label>
          </p>
          <hr />
        </div>
        <div className="pdf-upload-section">
          {!pdfFile ? (
            <div className="pdf-upload-area" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
              <input type="file" accept="application/pdf" onChange={handleFileUpload} id="pdf-upload-input" className="pdf-upload-input" />
              <label htmlFor="pdf-upload-input" className="pdf-upload-label">
                <img src={Icons.upload} alt="upload" className="pdf-icon-small" />
                <span className="pdf-upload-text">{t("pleaseUploadQuotationPdf")}</span>
                <span className="pdf-upload-hint">{t("or") || "Or"}</span>
                <span className="pdf-browse-btn">{t("browseFiles") || "Browse files"}</span>
              </label>
            </div>
          ) : (
            <div className="pdf-preview-area">
              <div className="pdf-preview-info">
                <img src={Icons.upload} alt="PDF" className="pdf-icon-small" />
                <div className="pdf-preview-details">
                  <span className="pdf-file-name">{pdfFile.name}</span>
                  <span className="pdf-file-size">{(pdfFile.size / 1024).toFixed(2)} KB</span>
                </div>
              </div>
              <div className="pdf-preview-actions">
                <button className="pdf-view-btn" onClick={() => openSecureFileUrl(quotationURL || pdfPreview)} disabled={isUploadingQuotation}>
                  {isUploadingQuotation ? t("loading") : t("view")}
                </button>
                <button className="pdf-remove-btn" onClick={handleRemovePdf}>
                  <img src={Icons.trashIcon} alt="Remove" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="form-section-footer">
        <button className="form-section-cancel" onClick={() => setPage(3)}>
          {t("back")}
        </button>
        <button className="form-section-create send-quote-btn" onClick={handleSendQuote}>
          {t("sendQuote")}
        </button>
      </div>
    </div>
  );
};

export default QuotationDetails;
